import {
  DEPOSIT_METRICS,
  depositSideMargin,
} from '../../screens/depositMetrics'
import { memo, useEffect, useRef } from 'react'
import {
  type LayoutChangeEvent,
  StyleSheet,
  useWindowDimensions,
} from 'react-native'
import Animated, {
  Easing,
  Keyframe,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'

import { Box, Text, useTheme } from '../../theme/restyle'
import {
  type AmountGlyph,
  type AmountGlyphKind,
  amountGlyphs,
} from '../../utils/v2/amountGlyphs'
import { amountRowGeometry } from '../../utils/v2/amountRowGeometry'
import {
  DIGIT_STAGGER,
  DIGIT_TRAVEL_RATIO,
  GLYPH_LAYOUT,
  MOTION_DURATION,
  MOTION_EASING,
  MOTION_TIMING,
  digitKeyframes,
} from '../../utils/motion'

export { fitScale } from '../../utils/v2/amountRowGeometry'

// Screen-specific measurements stay separate from the mirrored font tokens.
const AMOUNT_FONT_SIZE = DEPOSIT_METRICS.amount.size
const PLACEHOLDER_OPACITY = DEPOSIT_METRICS.amount.placeholderOpacity
// The line box must stay above the font's natural ~1.16em — iOS honours a
// shorter one by compressing the line and clipping the glyphs.
const LINE_HEIGHT = 120
const LETTER_SPACING = DEPOSIT_METRICS.amount.tracking
// The design trims to the cap box (68 at 96), which RN can't do — it lays out
// the line box. A negative margin takes that dead space back instead, so the
// glyphs overhang empty gaps rather than being clipped by their parent.
const CAP_BOX = 68

// Over-limit feedback (Figma "Over Limit"): the amount flashes this yellow.
// A design-page value, not a Fun Variables token.
const WARNING_COLOR = '#FFF530'

const AnimatedBox = Animated.createAnimatedComponent(Box)
const AnimatedText = Animated.createAnimatedComponent(Text)

const DIGIT_TRAVEL = LINE_HEIGHT * DIGIT_TRAVEL_RATIO
// Ease-in-out where the digits ease out: the comma barely moves through the
// first beat and then catches up, so it lands just behind the digits — the
// effect of a delay without a dead start.
const SEPARATOR_ENTER_EASING = Easing.bezier(0.65, 0, 0.35, 1)

// Every glyph motion is built from constants, so the set is built once.
// Digits grow up from the baseline; the placeholder and prefix pair keeps its
// full-line slide through the top of the row. Separators sit at grid positions
// rather than in the digit stream, so a length change has them jump: drop out
// below the baseline and pop back up at the new slot, trailing the digits on
// their slow-starting curve.
const MOTIONS = {
  digit: digitKeyframes(DIGIT_TRAVEL),
  separator: {
    entering: new Keyframe({
      0: { opacity: 0, transform: [{ translateY: DIGIT_TRAVEL }] },
      100: {
        opacity: 1,
        transform: [{ translateY: 0 }],
        easing: SEPARATOR_ENTER_EASING,
      },
    }).duration(MOTION_DURATION),
    exiting: new Keyframe({
      0: { opacity: 1, transform: [{ translateY: 0 }] },
      100: {
        opacity: 0,
        transform: [{ translateY: DIGIT_TRAVEL }],
        easing: MOTION_EASING,
      },
    }).duration(MOTION_DURATION),
  },
  above: slideKeyframes(-LINE_HEIGHT),
} as const

// Digit entrances differ only by their stagger delay, and there are a handful
// of those; built once per delay rather than per glyph per keystroke — every
// new keyframe object handed to an Animated view is re-registered on the UI
// thread, which was a round-trip per glyph per render.
const DIGIT_MOTIONS = new Map<number, ReturnType<typeof digitKeyframes>>()
function digitMotion(delay: number) {
  let motion = DIGIT_MOTIONS.get(delay)
  if (!motion) {
    motion = digitKeyframes(DIGIT_TRAVEL, delay)
    DIGIT_MOTIONS.set(delay, motion)
  }
  return motion
}

export interface AmountDisplayProps {
  /** The typed amount, or `''` for the placeholder. */
  value: string
  /** Written before the number. */
  prefix?: string
  /** Tints the amount, matching the message that replaces the balance line. */
  hasError?: boolean
  /** Over-limit flash: yellow (stronger than {@link hasError}). */
  hasWarning?: boolean
  accessibilityLabel: string
}

/**
 * The amount, read-only and driven by the numpad — it looks like a field but
 * isn't one, so tapping it must not open a keyboard.
 *
 * Each character is its own node so it can move on its own: digits and group
 * separators grow up from the baseline into the number and shrink back out,
 * while the placeholder lifts off the top instead.
 */
export function AmountDisplay({
  value,
  prefix = '$',
  hasError = false,
  hasWarning = false,
  accessibilityLabel,
}: AmountDisplayProps) {
  const theme = useTheme()
  const isEmpty = value === ''
  // Ghosting is for the untouched state only: deleting back to empty returns
  // the zero, but opaque (Figma "Amount Entry, Re-emptied").
  const hasEverTyped = useRef(false)
  if (!isEmpty) {
    hasEverTyped.current = true
  }
  const placeholderOpacity = hasEverTyped.current ? 1 : PLACEHOLDER_OPACITY
  const glyphs = amountGlyphs(value, prefix)
  const text = glyphs.map((glyph) => glyph.char).join('')

  const staggerOf = useMountStagger(glyphs)
  const { width } = useWindowDimensions()
  const gutter = 2 * depositSideMargin(width)
  const { onTrackLayout, onRowLayout, rowFit, rowPosition } = useFittedRow()
  // Both fade rather than snap: `''` → `0` keeps the same glyph, so the ghost
  // has to brighten in place.
  const prefixDimming = useDimming(isEmpty ? placeholderOpacity : 1)
  const placeholderDimming = useDimming(placeholderOpacity)
  const color = amountColor(theme, { hasError, hasWarning })

  return (
    // The window the characters slide through. What it clips is the dead space
    // the margin takes back, never the glyphs.
    <Box
      accessible
      accessibilityLabel={accessibilityLabel}
      accessibilityValue={{ text }}
      style={[styles.row, { marginVertical: -(LINE_HEIGHT - CAP_BOX) / 2 }]}
    >
      {/* A fixed layout origin lets short amounts glide to their new center;
          scaling lives inside the translation so it cannot scale the offset. */}
      <Box
        style={[styles.track, { marginHorizontal: gutter }]}
        onLayout={onTrackLayout}
      >
        <AnimatedBox style={[styles.slot, rowPosition]}>
          <AnimatedBox style={[styles.glyphRow, rowFit]} onLayout={onRowLayout}>
            {glyphs.map(({ key, char, kind }) => (
              <Glyph
                key={key}
                char={char}
                motion={glyphMotion(kind, isEmpty)}
                delay={staggerOf(key)}
                color={color}
                dimming={dimmingFor(kind, prefixDimming, placeholderDimming)}
              />
            ))}
          </AnimatedBox>
        </AnimatedBox>
      </Box>
    </Box>
  )
}

interface GlyphProps {
  char: string
  motion: ReturnType<typeof glyphMotion>
  /** Stagger for a bulk mount; a single keystroke passes 0. */
  delay: number
  color: string
  /** The ghosting fade: the placeholder's own, or the prefix's beside it. */
  dimming?: ReturnType<typeof useDimming>
}

/**
 * One character. Memoised so a keystroke re-renders the glyph that mounted
 * and the ones whose props changed — not the whole number.
 */
const Glyph = memo(function Glyph({
  char,
  motion,
  delay,
  color,
  dimming,
}: GlyphProps) {
  const entering =
    motion === 'digit' ? digitMotion(delay).entering : MOTIONS[motion].entering
  return (
    <AnimatedBox
      entering={entering}
      exiting={MOTIONS[motion].exiting}
      layout={GLYPH_LAYOUT}
    >
      {/* The fade rides on the text, not on the box: one view driving both an
          animated style and a layout animation breaks reanimated. */}
      <AnimatedText
        weight="medium"
        numberOfLines={1}
        style={[styles.glyph, { color }, dimming ?? null]}
      >
        {char}
      </AnimatedText>
    </AnimatedBox>
  )
})

/**
 * Left-to-right stagger for glyphs that mount together (a quick-amount chip
 * fills several at once); a single keystroke keeps zero delay. Previous keys
 * are compared post-commit, so each render sees the one before it.
 */
function useMountStagger(glyphs: AmountGlyph[]): (key: string) => number {
  const previousKeys = useRef<ReadonlySet<string>>(new Set())
  const mountingKeys = glyphs
    .filter(({ key }) => !previousKeys.current.has(key))
    .map(({ key }) => key)
  useEffect(() => {
    previousKeys.current = new Set(glyphs.map(({ key }) => key))
  })
  return (key) =>
    mountingKeys.length > 1
      ? Math.max(0, mountingKeys.indexOf(key)) * DIGIT_STAGGER
      : 0
}

/**
 * Centering and shrinking share a clock, with no parent layout animation.
 * - Layout measurements keep the target tied to the actual font metrics.
 * - Rapid taps retarget the current motion; duplicate layouts leave it alone.
 */
function useFittedRow() {
  const scale = useSharedValue(1)
  const translateX = useSharedValue(0)
  const trackWidth = useRef<number>(undefined)
  const rowWidth = useRef<number>(undefined)
  // The first fit lands without motion: nothing was on screen to shrink from.
  const previous = useRef<ReturnType<typeof amountRowGeometry>>(undefined)

  const fit = () => {
    if (trackWidth.current === undefined || rowWidth.current === undefined) {
      return
    }
    const next = amountRowGeometry(rowWidth.current, trackWidth.current)
    const last = previous.current
    previous.current = next
    if (!last) {
      scale.value = next.scale
      translateX.value = next.translateX
      return
    }
    if (last.scale !== next.scale) {
      scale.value = withTiming(next.scale, MOTION_TIMING)
    }
    if (last.translateX !== next.translateX) {
      translateX.value = withTiming(next.translateX, MOTION_TIMING)
    }
  }
  const onTrackLayout = (event: LayoutChangeEvent) => {
    trackWidth.current = event.nativeEvent.layout.width
    fit()
  }
  const onRowLayout = (event: LayoutChangeEvent) => {
    rowWidth.current = event.nativeEvent.layout.width
    fit()
  }

  const rowFit = useAnimatedStyle(() => {
    // Spelled out because the plugin workletizes by the hook's name, and esbuild
    // renames this one in the bundle.
    'worklet'
    return { transform: [{ scale: scale.value }] }
  })
  const rowPosition = useAnimatedStyle(() => {
    'worklet'
    return { transform: [{ translateX: translateX.value }] }
  })
  return { onTrackLayout, onRowLayout, rowFit, rowPosition }
}

/**
 * Eased opacity for the ghosted (untouched) state.
 * - Prefix and placeholder never remount, so they fade rather than animate in.
 * - The tween starts in an effect, not inside the style: there it would restart
 *   on every keystroke re-render.
 */
function useDimming(targetOpacity: number) {
  const opacity = useSharedValue(targetOpacity)
  useEffect(() => {
    opacity.value = withTiming(targetOpacity, MOTION_TIMING)
  }, [opacity, targetOpacity])
  return useAnimatedStyle(() => {
    // Spelled out because the plugin workletizes by the hook's name, and esbuild
    // renames this one (`useAnimatedStyle2`) in the bundle.
    'worklet'
    return { opacity: opacity.value }
  })
}

function amountColor(
  theme: { content: { elements: { primary: string; error: string } } },
  { hasError, hasWarning }: { hasError: boolean; hasWarning: boolean },
) {
  if (hasWarning) {
    return WARNING_COLOR
  }
  if (hasError) {
    return theme.content.elements.error
  }
  return theme.content.elements.primary
}

function dimmingFor(
  kind: AmountGlyphKind,
  prefix: ReturnType<typeof useDimming>,
  placeholder: ReturnType<typeof useDimming>,
) {
  if (kind === 'prefix') {
    return prefix
  }
  if (kind === 'placeholder') {
    return placeholder
  }
  return undefined
}

/**
 * Which motion a character mounts and unmounts with. Digits grow from the
 * baseline; separators jump through the baseline instead of gliding; the
 * placeholder — and the prefix while it stands beside it — lifts off the top,
 * so the pair never crosses the digits.
 */
function glyphMotion(kind: AmountGlyphKind, isEmpty: boolean) {
  if (kind === 'placeholder') {
    return 'above'
  }
  if (kind === 'prefix' && isEmpty) {
    return 'above'
  }
  if (kind === 'separator') {
    return 'separator'
  }
  return 'digit'
}

/** Enters from `offset` and leaves back to it — a negative one runs upwards. */
function slideKeyframes(offset: number) {
  return {
    entering: new Keyframe({
      0: { transform: [{ translateY: offset }] },
      100: { transform: [{ translateY: 0 }], easing: MOTION_EASING },
    }).duration(MOTION_DURATION),
    exiting: new Keyframe({
      0: { transform: [{ translateY: 0 }] },
      100: { transform: [{ translateY: offset }], easing: MOTION_EASING },
    }).duration(MOTION_DURATION),
  }
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignSelf: 'stretch',
    overflow: 'hidden',
  },
  // Takes the row's full width (minus gutters) regardless of content, so the
  // measured track width is the allowance and not the glyph row's own width.
  track: { flex: 1 },
  // Full-width and left-aligned: Yoga must not also move the animated row.
  slot: { alignSelf: 'stretch' },
  // Preserve natural width beyond the track, then scale from the left edge.
  glyphRow: {
    flexDirection: 'row',
    flexShrink: 0,
    alignSelf: 'flex-start',
    transformOrigin: 'left center',
  },
  glyph: {
    fontSize: AMOUNT_FONT_SIZE,
    lineHeight: LINE_HEIGHT,
    letterSpacing: LETTER_SPACING,
    fontVariant: [],
  },
})
